import React, { useState, useEffect } from 'react';
import { ChevronDown, Loader2, Smile, Meh, Frown } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { Skeleton } from '../../../skeletons';
import residencesService from '../../../services/carehome_manager/residencesService';

const BehaviourTab = () => {
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({
        records: [],
        chartData: []
    });

    useEffect(() => {
        const fetchBehaviourData = async () => {
            try {
                setLoading(true);
                const response = await residencesService.getBehaviourData(id);
                setData(response.data);
            } catch (err) {
                console.error('Error fetching behaviour data:', err);
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchBehaviourData();
        }
    }, [id]);

    const generateChartPath = (chartData, maxValue) => {
        if (!chartData || chartData.length === 0) return "";
        const width = 1000;
        const height = 200;
        const stepX = width / (chartData.length - 1);
        
        const points = chartData.map((d, i) => {
            const x = i * stepX;
            const y = height - (maxValue > 0 ? (d.value / maxValue) * height : height/2);
            return `${x},${y}`;
        });

        return `M ${points[0]} ${points.map((p, i) => i === 0 ? '' : `L ${p}`).join(' ')}`;
    };

    const generateAreaPath = (chartPath) => {
        if (!chartPath) return "";
        return `${chartPath} L 1000,200 L 0,200 Z`;
    };

    const BehaviourChart = () => {
        const maxValue = 100;
        const path = generateChartPath(data.chartData, maxValue);
        const areaPath = generateAreaPath(path);

        return (
            <div className="w-full">
                <div className="flex items-center justify-between mb-8">
                    <h3 className="text-[16px] md:text-[18px] font-medium text-[#1E293B] dark:text-gray-100 tracking-tight italic">Mood Score Trend</h3>
                    <div className="flex gap-3">
                        <button className="flex items-center gap-2 px-3 py-1.5 border border-gray-200 dark:border-gray-800 rounded-md text-[12px] md:text-[13px] font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#31373f] transition-colors">
                            Last 7 Days
                        </button>
                    </div>
                </div>

                <div className="flex">
                    <div className="w-[45px] flex flex-col justify-between text-[11px] font-medium text-[#1E293B] dark:text-gray-400 pb-[30px] pr-2 h-[200px] text-right">
                        <span className="text-green-500">HAPPY</span>
                        <span className="text-blue-500">OK</span>
                        <span className="text-red-500">SAD</span>
                        <span>0</span>
                    </div>

                    <div className="flex-1 relative h-[200px]">
                        <svg viewBox="0 0 1000 200" className="w-full h-full overflow-visible" preserveAspectRatio="none">
                            <defs>
                                <linearGradient id="gradMood" x1="0%" y1="0%" x2="0%" y2="100%">
                                    <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.3" />
                                    <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
                                </linearGradient>
                            </defs>
                            <line x1="0" y1="0" x2="1000" y2="0" stroke="#f8fafc" strokeWidth="1.5" className="dark:stroke-gray-800" />
                            <line x1="0" y1="100" x2="1000" y2="100" stroke="#f8fafc" strokeWidth="1.5" className="dark:stroke-gray-800" />
                            <line x1="0" y1="200" x2="1000" y2="200" stroke="#f8fafc" strokeWidth="1.5" className="dark:stroke-gray-800" />

                            {path && (
                                <>
                                    <path d={areaPath} fill="url(#gradMood)" />
                                    <path d={path} fill="none" stroke="#3b82f6" strokeWidth="3" />
                                    {data.chartData.map((d, i) => (
                                        <circle 
                                            key={i} 
                                            cx={i * (1000 / (data.chartData.length - 1))} 
                                            cy={200 - (maxValue > 0 ? (d.value / maxValue) * 200 : 100)} 
                                            r="5" 
                                            fill="#3b82f6" 
                                            stroke="white" 
                                            strokeWidth="2" 
                                        />
                                    ))}
                                </>
                            )}
                        </svg>
                        <div className="absolute -bottom-8 w-full flex justify-between text-[11px] font-medium text-[#1E293B] dark:text-gray-400">
                            {data.chartData.map(d => (
                                <span key={d.date}>{new Date(d.date).toLocaleDateString('en-GB', { weekday: 'short' })}</span>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="w-full pt-2 animate-in fade-in duration-300">
            <h2 className="text-[20px] font-semibold text-[#1E293B] dark:text-gray-100 mb-6">Behaviour & Emotional Record</h2>

            {/* Chart Section */}
            <div className="bg-white dark:bg-[#262b33] rounded-xl shadow-[0_2px_8px_-4px_rgba(0,0,0,0.05)] p-6 md:p-8 pb-12 mb-8 border border-gray-50 dark:border-gray-800">
                {loading ? (
                    <Skeleton className="h-[200px] w-full rounded-xl" />
                ) : (
                    <BehaviourChart />
                )}
            </div>

            {/* Records Section */}
            <div>
                <div className="flex items-center justify-between mb-4 px-1">
                    <h3 className="text-[17px] font-semibold text-[#1E293B] dark:text-gray-100 italic">Recent behaviour records</h3>
                    <button className="text-[13px] font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors">See all</button>
                </div>

                {/* Table Header */}
                <div className="grid grid-cols-[1fr_1fr_1fr_2fr] gap-4 px-6 py-2 text-[13px] font-medium text-gray-500 dark:text-gray-400 mb-2">
                    <div>Staff name</div>
                    <div>Date</div>
                    <div>Behaviour / Mood</div>
                    <div>Description / Notes</div>
                </div>

                {/* Table Rows */}
                <div className="flex flex-col gap-3">
                    {loading ? (
                        [1, 2, 3].map(i => (
                            <div key={i} className="h-20 bg-gray-50 dark:bg-[#262b33] animate-pulse rounded-xl border border-gray-50 dark:border-gray-800" />
                        ))
                    ) : data.records.length > 0 ? (
                        data.records.map((row) => (
                            <div
                                key={row.id}
                                className="grid grid-cols-[1fr_1fr_1fr_2fr] gap-4 items-center px-6 py-5 bg-white dark:bg-[#262b33] rounded-xl shadow-[0_2px_8px_-4px_rgba(0,0,0,0.05)] border border-gray-50 dark:border-gray-800"
                            >
                                <div className="text-[14px] font-semibold text-[#1E293B] dark:text-gray-200">{row.staff}</div>
                                <div className="text-[14px] font-medium text-[#475569] dark:text-gray-400">{row.date}</div>
                                <div className="text-[14px] font-bold text-blue-600 dark:text-blue-400">{row.behaviour}</div>
                                <div className="text-[13px] text-[#475569] dark:text-gray-400 leading-relaxed pr-4">{row.description}</div>
                            </div>
                        ))
                    ) : (
                        <div className="py-12 text-center text-gray-500 dark:text-gray-400 bg-white dark:bg-[#262b33] rounded-xl border border-dashed border-gray-200 dark:border-gray-800">
                            No behaviour records found for this period.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BehaviourTab;